import { stringify } from 'query-string';
import fetchJson from './fetch';

import {
    GET_LIST,
    GET_ONE,
    GET_MANY,
    GET_MANY_REFERENCE,
    CREATE,
    UPDATE,
    UPDATE_MANY,
    DELETE,
    DELETE_MANY,
} from 'react-admin';

export * from './authProvider';
export { default as storage } from './storage';

export default (apiUrl, httpClient = fetchJson) => {
  /**
   * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
   * @param {String} resource Name of the resource to fetch, e.g. 'posts'
   * @param {Object} params The data request params, depending on the type
   * @returns {Object} { url, options } The HTTP request parameters
   */
  const convertDataRequestToHTTP = (type, resource, params) => {
    let url = '';
    const options = {};
    const specialParams = ['pagination', 'sort', 'filter', 'queryParams'];
    switch (type) {
      case GET_LIST: {
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;
        const query = {};
        query['where'] = {...params.filter};
        if (field) query['order'] = [field + ' ' + order];
        if (perPage >= 0) query['limit'] = perPage;
        if ((perPage > 0) && (page >= 0)) query['skip'] = (page - 1) * perPage;

        Object.keys(params).forEach(key => {
          if (!specialParams.includes(key) && params[key] !== undefined)
            query[key] = params[key];
        });
        // inhouse query params 
        const queryParams = params.queryParams ? params.queryParams : {};
        url = `${apiUrl}/${resource}?${stringify({ filter: JSON.stringify(query), clientType: 'bo', queryParams: JSON.stringify(queryParams) })}`;
        break;
      }
      case GET_ONE: {
        url = `${apiUrl}/${resource}/${params.id}`;
        // 추가 params 데이터는 query string 처리. 
        const query = JSON.parse(JSON.stringify(params));
        query.clientType = 'bo';
        delete query.id;
        url += '?' + stringify(query);
        break;
      }
      case GET_MANY: {
        const listId = params.ids.map(id => {
          return {id};
        });
        let query = '';
        if (listId.length > 0) {
          const filter = {
            where: {or: listId},
          };
          query = `?${stringify({filter: JSON.stringify(filter)})}`;
        }
        url = `${apiUrl}/${resource}${query}`;
        break;
      }
      case GET_MANY_REFERENCE: {
        const { page, perPage } = params.pagination;
        const { field, order } = params.sort;
        const query = {};
        query['where'] = {...params.filter};
        query['where'][params.target] = params.id;
        if (field) query['order'] = [field + ' ' + order];
        if (perPage >= 0) query['limit'] = perPage;
        if ((perPage > 0) && (page >= 0)) query['skip'] = (page - 1) * perPage;

        Object.keys(params).forEach(key => {
          if (!specialParams.includes(key) && params[key] !== undefined)
            query[key] = params[key];
        });

        url = `${apiUrl}/${resource}?${stringify({filter: JSON.stringify(query)})}`;
        break;
      }
      case UPDATE: {
        url = `${apiUrl}/${resource}/${params.id}`;
        options.method = 'PATCH';
        options.body = JSON.stringify(params.data);
        break;
      }
      case CREATE: {
        url = `${apiUrl}/${resource}`;
        options.method = 'POST';
        options.body = JSON.stringify(params.data);
        break;
      }
      case DELETE:
        url = `${apiUrl}/${resource}/${params.id}`;
        options.method = 'DELETE';
        break;
      default:
        throw new Error(`Unsupported fetch action type ${type}`);
    }
    return { url, options };
  };

  /**
   * @param {Object} response HTTP response from fetch()
   * @param {String} type One of the constants appearing at the top if this file, e.g. 'UPDATE'
   * @param {String} resource Name of the resource to fetch, e.g. 'posts'
   * @param {Object} params The data request params, depending on the type
   * @returns {Object} Data response
   */
  const convertHTTPResponse = (response, type, resource, params) => {
    const { headers, json } = response;
    switch (type) {
      case GET_LIST:
      case GET_MANY_REFERENCE:
        if (!headers.has('content-range')) {
          throw new Error(
            'The Content-Range header is missing in the HTTP Response. The simple REST data provider expects responses for lists of resources to contain this header with the total number of results to build the pagination. If you are using CORS, did you declare Content-Range in the Access-Control-Expose-Headers header?'
          );
        }
        return {
          data: json,
          total: parseInt(
            headers
              .get('content-range')
              .split('/')
              .pop(),
              10
          ),
        };
      case CREATE:
        return { data: { ...params.data, id: json.id } };
      case DELETE:
        return { data: { ...json, id: params.id } };
      default:
        return { data: json };
    }
  };

  /**
   * @param {string} type Request type, e.g GET_LIST
   * @param {string} resource Resource name, e.g. "posts"
   * @param {Object} payload Request parameters. Depends on the request type
   * @returns {Promise} the Promise for a data response
   */
  return (type, resource, params) => {
    // simple-rest doesn't handle filters on UPDATE route, so we fallback to calling UPDATE n times instead
    if (type === UPDATE_MANY) {
      return Promise.all(
        params.ids.map(id =>
          httpClient(`${apiUrl}/${resource}/${id}`, {
            method: 'PUT',
            body: JSON.stringify(params.data),
          })
        )
      ).then(responses => ({
        data: responses.map(response => response.json),
      }));
    }
    // simple-rest doesn't handle filters on DELETE route, so we fallback to calling DELETE n times instead
    if (type === DELETE_MANY) {
      return Promise.all(
        params.ids.map(id =>
          httpClient(`${apiUrl}/${resource}/${id}`, {
            method: 'DELETE',
          })
        )
      ).then(responses => ({
        data: responses.map(response => response.json),
      }));
    }

    // file upload 처리 
    if (type === CREATE || type === UPDATE) {

      const specs = getUploadSpec(params.data);
      if (specs.length > 0) {
        // prepare form data 
        const formData = new FormData();
        specs.forEach(s => formData.append('', s.file, s.file.name));

        // upload files then send the original request
        return httpClient(`${apiUrl}/files/files.fooding.io/upload`, {
          method: 'POST',
          body: formData
        })
        .then(response => {
          // console.log(`uploaded] specs: ${JSON.stringify(specs)}`)
          // console.log(`uploaded] response.json: ${JSON.stringify(response.json)}`)

          // update data 
          response.json.forEach((item, i) => {
            const spec = specs[i];
            if (spec.isArray) {
              // id 만 업로드 하도록 api 변경됨 
              // const srcData = params.data[spec.key][spec.index];
              // Object.assign(srcData, item);
              // delete srcData.rawFile;
              params.data[spec.key].push(item.id);
              params.data[spec.key] = params.data[spec.key].filter(el => typeof el === 'number');
            }
            else if (spec.key.endsWith('__Ref')) {
              const k = spec.key.substring(0, spec.key.length - 5)
              params.data[k] = item.id;
              delete params.data[spec.key];
            }
            else {
              // id 만 업로드 하도록 api 변경됨 
              // Object.assign(params.data[spec.key], item);
              // delete params.data[spec.key].rawFile;
              params.data[spec.key] = item.id;
            }
          });

          //console.table(params.data)

          // original request 
          const { url, options } = convertDataRequestToHTTP(
            type,
            resource,
            params
          );

          return httpClient(url, options);
        })
        .then(response => 
          convertHTTPResponse(response, type, resource, params)
        )
        // TODO catch here? 

      }
    }

    const { url, options } = convertDataRequestToHTTP(
      type,
      resource,
      params
    );
    
    return httpClient(url, options).then(response =>
        convertHTTPResponse(response, type, resource, params)
    );
  };
};

// 1 depth child 조사
// spec [{ key, isArray, index, file }]
function getUploadSpec(data) {
  const specs = [];

  Object.keys(data).forEach(k => {
    const child = data[k];
    if (Array.isArray(child)) {
      child.forEach((item, i) => {
        if (item && item.hasOwnProperty('rawFile') && item.rawFile instanceof File) {
          specs.push({
            key: k,
            isArray: true,
            index: i,
            file: item.rawFile
          });
        }
      });
    }
    else if (child && child.hasOwnProperty('rawFile') && child.rawFile instanceof File) {
      specs.push({
        key: k,
        isArray: false,
        file: child.rawFile
      });
    }
  });

  return specs;
}